import React, { useEffect, useState } from 'react';
import { Modal, Box, ButtonGroup, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Styles from '../../../common/styles/styles';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { parseDayJsDate } from '../../../common/utils/massageDataUtil';
import "react-datepicker/dist/react-datepicker.css";

export default function AdminAlertModal({ alert, onTriggerCreate, onTriggerUpdate }) {

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  
  const [formData, setFormData] = useState({
      id: alert.id,
      sensor: alert.sensor, 
      events: alert.events, 
      severity: alert.severity, 
      first_seen: alert.first_seen, 
      last_seen: alert.last_seen, 
      sparkline: alert.sparkline, 
      source: alert.source, 
      message: alert.message, 
      phen_ignore: alert.phen_ignore, 
      reference: alert.reference
  });

  const minDate = new Date("2024/01/01" );

  const handleOpen = () => {
    setFormData({ ...formData });
    setOpen(true);
    setTitle('Create Alert');
  }

  const handleEditOpen = () => {
    if (alert.first_seen !== '') {
      const firstSeenObject = new Date(alert.first_seen);
      const firstSeenDate = dayjs(`${firstSeenObject.getMonth()+1}-${firstSeenObject.getDate()}-${firstSeenObject.getFullYear()} ${firstSeenObject.getHours()}:${firstSeenObject.getMinutes()}`);
      
      const lastSeenObject = new Date(alert.last_seen);
      const lastSeenDate = dayjs(`${lastSeenObject.getMonth()+1}-${lastSeenObject.getDate()}-${lastSeenObject.getFullYear()} ${lastSeenObject.getHours()}:${lastSeenObject.getMinutes()}`);
      
      setFormData({ ...formData, first_seen: firstSeenDate, last_seen: lastSeenDate });
      
    }
    setOpen(true);
    setTitle('Update Alert');    
  }

  const handleUpdate = async (event) => {
      await editAlert(event);
      setOpen(false);
      if (title === 'Create Alert') { 
        onTriggerCreate(formData);
      } else {
        onTriggerUpdate(formData);
      }
  }

  const handleClose = () => setOpen(false);

  const handleInputChange = (field) => (event) => {
      if (field === 'first_seen') {
        const date = parseDayJsDate(event);
        setFormData({ ...formData, [field]: event, first_seen_string: date });
      } else if (field === 'last_seen') {
        const date = parseDayJsDate(event);
        setFormData({ ...formData, [field]: event, last_seen_string: date });
      } else {
        const value = event.target.value;
        setFormData({ ...formData, [field]: value });
      }
  };
  
  const editAlert = async (event) => {
    try {
        event.preventDefault();
        
        let response = null;
        if (title === 'Create Alert') {
            response = await fetch(`${process.env.REACT_APP_API}/alerts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
        } else {
            response = await fetch(`${process.env.REACT_APP_API}/alerts/${formData.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
      console.error('Network error.  Please try again: ', error);
    }
  }

  const severityTypes = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ]

  const options = [
    { value: true, label: 'True' },
    { value: false, label: 'False' }
  ]
  
  const textFieldStyles = Styles.textFieldStyles;
  const dateFieldStyles = Styles.dateFieldStyles;

  useEffect(() => {
    if (alert.id !== undefined) {
      setFormData(alert);
    } 
  }, [alert]);

  return (
    <>

        { alert === "" && 
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleOpen} >
            Add New
          </Button>
        }
        
        { alert !== '' && 
          <Button variant="outlined" color="success" size="small" onClick={handleEditOpen}>
              <EditIcon />
          </Button>
        }
        
        <Modal 
          open={open} 
          onClose={handleClose}
          style={{
            overlay: {
              backgroundColor: '#080030', // Semi-transparent black
            },
            content: {
              backgroundColor: '#080030',
            },
          }}
        >
            <Box sx={{color: 'white', p: 4}}>
                <Box
                  component={Paper}
                  elevation={3}
                  sx={{
                    mx: 'auto',
                    width: 400,
                    bgcolor: '#1e1e1e',
                    p: 2,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    height: '60%', 
                    width: '85%', 
                    maxWidth: '400px',
                    overflowY: 'auto', 
                  }}
                >
                  <Typography variant="h4" component="h2" color="white" mb={2}>
                    {title}
                  </Typography>

                  <Box component="form" onSubmit={handleUpdate}>
                    <Stack spacing={2}>
                      {
                        title === 'Create Alert' ? 
                          <TextField
                              fullWidth
                              label="ID"
                              value={formData.id}
                              onChange={handleInputChange('id')}
                              sx={ textFieldStyles }
                          />
                        :
                          null
                      }
                        <TextField
                            fullWidth
                            label="Reporter"
                            value={formData.sensor}
                            onChange={handleInputChange('sensor')}
                            sx={ textFieldStyles }
                        />
                        <TextField
                            fullWidth
                            label="Events"
                            value={formData.events}
                            onChange={handleInputChange('events')}
                            sx={ textFieldStyles }
                        />
                        <TextField
                            select
                            fullWidth
                            label="Severity"
                            value={formData.severity}
                            onChange={handleInputChange('severity')}
                            sx={ textFieldStyles }
                        >
                          {severityTypes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker 
                          label="First Seen" 
                          value={formData.first_seen}
                          showTimeSelect
                          timeIntervals={15}
                          onChange={handleInputChange('first_seen')}
                          sx={ dateFieldStyles }
                          slotProps={{  textField: { variant: 'outlined' } }}
                            minDate={dayjs().subtract(2, 'year')}
                        />
                        <DateTimePicker
                          label="Last Seen"
                          value={formData.last_seen}
                          onChange={handleInputChange('last_seen')}
                          showTimeSelect
                          timeIntervals={15}
                          sx={ dateFieldStyles }
                          minDate={dayjs().subtract(2, 'year')}
                        />
                      </LocalizationProvider>
                      <TextField
                        fullWidth
                        label="Sparkline"
                        value={formData.sparkline}
                        onChange={handleInputChange('sparkline')}
                        sx={ textFieldStyles }
                      />
                      <TextField
                        fullWidth
                        label="Source"
                        value={formData.source}
                        onChange={handleInputChange('source')}
                        sx={ textFieldStyles }
                      />
                      <TextField
                        fullWidth
                        label="Alert Message"
                        value={formData.message}
                        onChange={handleInputChange('message')}
                        sx={ textFieldStyles }
                      />
                      <TextField
                        select
                        fullWidth
                        label="Phen.AI Ignore"
                        value={formData.phen_ignore}
                        onChange={handleInputChange('phen_ignore')}
                        sx={ textFieldStyles }
                      >
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </TextField>
                      <TextField
                        fullWidth
                        label="Reference"
                        value={formData.reference}
                        onChange={handleInputChange('reference')}
                        sx={ textFieldStyles }
                      />
                  <ButtonGroup 
                    size="small"
                    variant="contained" 
                    aria-label="Basic button group" 
                    fullWidth
                    sx={{ mt:2 }}
                  >
                    <Button
                      onClick={handleClose}
                      type="cancel"
                      variant="contained"
                      sx={ Styles.buttonStyle }
                    >
                      Cancel
                    </Button>              
                    <Button
                      type="submit"                      
                      variant="contained"
                      sx={{ ...Styles.buttonStyle,  ml:4 }}
                    >
                      {title}
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Box>
            </Box>
          </Box>          
        </Modal>
    </>
  );
}